import React from 'react';
import { Box, Container, Typography, Grid, IconButton, Stack } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import afaqImage from '../assets/img/afaq-ahmad.jpg';
import markImage from '../assets/img/mark-smeke.jpg';
import merlinImage from '../assets/img/merlin-hernandez.jpg';
import jadeImage from '../assets/img/jade-wolfe.jpg';
import leonardImage from '../assets/img/leonard-muldoon.jpg';
import carlImage from '../assets/img/carl-gregory.jpg';

interface SocialLink {
  icon: React.ReactNode;
  href: string;
}

interface TeamMember {
  picture: string;
  fullName: string;
  designation: string;
  bio: string;
  socialLinks: SocialLink[];
}

const teamMembers: TeamMember[] = [
  {
    picture: afaqImage,
    fullName: "Afaq Ahmad",
    designation: "Chairperson",
    bio: "Leading ACM at ACC with a vision to connect students to the computing world.",
    socialLinks: [
      { icon: <LinkedInIcon />, href: "https://www.linkedin.com/in/afaqahmadijaz/" },
      { icon: <GitHubIcon />, href: "https://github.com/afaqahmadijaz" },
      { icon: <EmailIcon />, href: "mailto:afaq.ahmad@g.austincc.edu" },
    ],
  },
  {
    picture: markImage,
    fullName: "Mark Alexander Smeke",
    designation: "Vice Chair",
    bio: "Dedicated to organizing innovative events and fostering collaboration at ACM ACC.",
    socialLinks: [
      { icon: <LinkedInIcon />, href: "https://www.linkedin.com/in/mark-smeke-97b15328a/" },
      { icon: <GitHubIcon />, href: "https://github.com/alexandersmeke" },
      { icon: <EmailIcon />, href: "mailto:mark.smeke@g.austincc.edu" },
    ],
  },
  {
    picture: merlinImage,
    fullName: "Merlin Gabriel Hernandez",
    designation: "Treasurer",
    bio: "Ensuring seamless financial operations for ACM ACC initiatives.",
    socialLinks: [
      { icon: <LinkedInIcon />, href: "https://www.linkedin.com/in/merlin-hdz/" },
      { icon: <GitHubIcon />, href: "https://github.com/MerlinHdz" },
      { icon: <EmailIcon />, href: "mailto:merlin.hernandez@g.austincc.edu" },
    ],
  },
  {
    picture: jadeImage,
    fullName: "Jade Wolfe",
    designation: "Secretary",
    bio: "Keeping ACM ACC organized and ensuring smooth communication across the team.",
    socialLinks: [
      { icon: <GitHubIcon />, href: "https://github.com/Jade-Wolfe" },
      { icon: <EmailIcon />, href: "mailto:jade.wolfe@g.austincc.edu" },
    ],
  },
  {
    picture: leonardImage,
    fullName: "Leonard Muldoon",
    designation: "Membership Chair",
    bio: "Helping grow ACM ACC's member base through outreach and engaging initiatives.",
    socialLinks: [
      { icon: <LinkedInIcon />, href: "https://www.linkedin.com/in/leonard-muldoon-bb6b90257/" },
      { icon: <GitHubIcon />, href: "https://github.com/lennymuldoon" },
      { icon: <EmailIcon />, href: "mailto:leonard.muldoon@g.austincc.edu" },
    ],
  },
  {
    picture: carlImage,
    fullName: "Carl Gregory",
    designation: "Faculty Sponsor",
    bio: "Guiding ACM ACC with academic and professional expertise.",
    socialLinks: [
    ],
  },
];

interface TeamMemberItemProps {
  member: TeamMember;
}

const TeamMemberItem: React.FC<TeamMemberItemProps> = ({ member }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 4,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.12)',
      },
    }}
  >
    <Box
      sx={{
        width: 150,
        height: 150,
        borderRadius: '50%',
        border: 3,
        borderColor: 'purple.main',
        p: 0.5,
        mb: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={member.picture}
        alt={member.fullName}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          objectFit: 'cover',
        }}
      />
    </Box>

    <Typography
      variant="h5"
      sx={{
        fontSize: '1.5rem',
        fontWeight: 600,
        mb: 1,
        color: 'text.primary',
      }}
    >
      {member.fullName}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{
        fontSize: '1rem',
        color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'purple.main',
        fontWeight: 500,
        mb: 2,
      }}
    >
      {member.designation}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        mb: 3,
        maxWidth: '280px',
        mx: 'auto',
        lineHeight: 1.6,
      }}
    >
      {member.bio}
    </Typography>

    <Stack 
      direction="row" 
      spacing={2} 
      justifyContent="center"
      sx={{ mt: 'auto' }}
    >
      {member.socialLinks.map((item, index) => (
        <IconButton
          key={index}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          sx={{
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: 'purple.main',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {item.icon}
        </IconButton>
      ))}
    </Stack>
  </Box>
);

const TeamSection: React.FC = () => {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            Our Team
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: 'md', 
              mx: 'auto',
              color: 'text.secondary',
              fontSize: '1.125rem',
            }}
          >
            Meet the passionate individuals driving innovation at ACM ACC.
          </Typography>
        </Box>

        <Grid 
          container 
          spacing={4} 
          sx={{ 
            maxWidth: 1200, 
            mx: 'auto',
          }}
        >
          {teamMembers.map((member, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <TeamMemberItem member={member} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TeamSection;